import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Stethoscope, HeartHandshake, Zap, Sprout, TrendingUp, BookOpen, ArrowRight, Users, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

// Fallback visuals for categories that don't specify their own icon/color —
// keeps the existing look for known slugs while still supporting new
// categories created in the admin without a code change.
const ICONS = { education:GraduationCap, health:Stethoscope, women:HeartHandshake, youth:Zap, environment:Sprout, livelihood:TrendingUp, other:BookOpen };
const GRAD  = { education:'from-blue-600 to-blue-800', health:'from-emerald-600 to-emerald-800', women:'from-pink-600 to-rose-800', youth:'from-violet-600 to-purple-800', environment:'from-green-600 to-teal-800', livelihood:'from-amber-600 to-orange-800', other:'from-gray-600 to-gray-800' };
const FALLBACK_ICON = BookOpen;
const FALLBACK_GRAD = 'from-gray-600 to-gray-800';

export default function ProgramsPage() {
  const [programs, setPrograms]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [cat, setCat]               = useState('');

  useEffect(() => {
    api.get('/program-categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const categoryName = (slug) => categories.find(c => c.slug === slug)?.name || slug;

  useEffect(() => {
    setLoading(true);
    api.get(`/programs?limit=50${cat?`&category=${cat}`:''}`).then(r => setPrograms(r.data.items||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, [cat]);

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">What We Do</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">Our Programs</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">Data-driven research, human-centered AI integration, and institutional capacity development transforming education across West Africa.
</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-2 flex-wrap justify-center mb-10">
              <button onClick={() => setCat('')}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all capitalize ${cat==='' ? 'bg-royal-800 border-royal-800 text-white' : 'border-gray-200 text-gray-600 hover:border-royal-300 bg-white'}`}>
                All Programs
              </button>
              {categories.map(c => (
                <button key={c.slug} onClick={() => setCat(c.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all capitalize ${cat===c.slug ? 'bg-royal-800 border-royal-800 text-white' : 'border-gray-200 text-gray-600 hover:border-royal-300 bg-white'}`}>
                  {c.name}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48 gap-2 text-gray-400"><Loader2 size={22} className="animate-spin" /> Loading programs...</div>
            ) : programs.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
                <p>No programs found. Check back soon.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {programs.map(prog => {
                  const Icon = ICONS[prog.category] || FALLBACK_ICON;
                  const grad = GRAD[prog.category] || FALLBACK_GRAD;
                  return (
                    <Link key={prog._id} to={`/programs/${prog._id}`}
                      className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1.5 overflow-hidden border border-gray-100">
                      <div className={`relative h-44 bg-gradient-to-br ${grad} overflow-hidden`}>
                        {prog.image
                          ? <img src={prog.image} alt={prog.title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
                          : <div className="flex items-center justify-center h-full"><Icon size={48} className="text-white/20" /></div>}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        <div className="absolute top-3 left-3 flex gap-2">
                          <span className="px-2.5 py-1 bg-white/15 backdrop-blur-sm text-white text-[10px] font-bold rounded-full border border-white/20 uppercase tracking-wide capitalize flex items-center gap-1">
                            <Icon size={10} />{categoryName(prog.category)}
                          </span>
                          <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full capitalize ${prog.status==='active'?'bg-emerald-500 text-white':prog.status==='upcoming'?'bg-gold-500 text-royal-950':'bg-gray-500 text-white'}`}>
                            {prog.status}
                          </span>
                        </div>
                        {prog.beneficiaries && <div className="absolute bottom-3 right-3 text-right"><div className="text-white font-bold text-lg leading-none">{prog.beneficiaries.toLocaleString()}+</div><div className="text-white/60 text-[10px]">beneficiaries</div></div>}
                      </div>
                      <div className="p-5">
                        <h3 className="font-display font-semibold text-gray-900 mb-2 group-hover:text-royal-800 transition-colors leading-snug">{prog.title}</h3>
                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-4">{prog.shortDescription || prog.description}</p>
                        {prog.location && <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3"><span>📍</span>{prog.location}</div>}
                        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal-800 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={14} /></div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}